import { useEffect, useMemo, useState } from 'react'

import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import type { FeeAutoConsiderPolicy } from '@ebalance/proto/bffuser/v1/bid_limit_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Flex,
  Link,
  TabsComponent as Tabs,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WorkflowPageLayout } from '~/components/common/WorkflowPageLayout'
import { tabTriggerFontSize } from '~/constants/tabStyle'
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import { useBidLimitMatrixDraft } from '~/pages/AdminBidLimits/hooks/useBidLimitMatrixDraft'
import { useGetBidLimitMatrixScreen } from '~/pages/AdminBidLimits/hooks/useBidLimitSettings'
import { useFeeUnitPrices } from '~/pages/AdminBidLimits/hooks/useFeeUnitPrices'
import { IntradayTab } from '~/pages/AdminBidLimits/tabs/IntradayTab'
import { SpotBlockTab } from '~/pages/AdminBidLimits/tabs/SpotBlockTab'
import { SpotNormalTab } from '~/pages/AdminBidLimits/tabs/SpotNormalTab'
import { routePaths } from '~/routes/routePaths'

type TabKey = 'spot_normal' | 'spot_block' | 'intraday'

const DEFAULT_TAB: TabKey = 'spot_normal'

export const AdminBidLimitsPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const [tab, setTab] = useState<TabKey>(DEFAULT_TAB)

  // workflow-back-bar-readonly AC-06/AC-17: caseId は URL クエリパラメータ
  // `?workflowId=` から取得し、read-only 制御 (完了済み
  // ケースでタグ追加/保存/削除・マトリクス保存/リセット・市場価格注文ポリシー
  // トグルを無効化) に使う (fail-closed の理由は useCurrentWorkflowCase の JSDoc
  // 参照)。
  const { isReadonly } = useCurrentWorkflowCase()

  const { data, errorUpdatedAt, isFetching, isLoading, refetch } =
    useGetBidLimitMatrixScreen()
  const draft = useBidLimitMatrixDraft()
  const feeUnitPrices = useFeeUnitPrices()

  // AC M-12: GetBidLimitMatrixScreen 失敗時に isLoading のみを見ていると
  // 「isLoading=false かつ data=undefined」のまま空/loading風表示で止まり、
  // エラーだと気づけない (リスク M-12)。他画面 (ProcurementPlanDetail /
  // SupplyGap) と同じ Alert + 再読み込み導線で示す。
  //
  // 素の `isError` は使わない: React Query は refetch 開始時に status を
  // 'error' → 'pending' へ、error を null にリセットするため、初回取得が
  // 一度も成功していない画面で `isError` だけを見ると refetch 中に false へ
  // 落ちてエラー表示が一瞬 loading 風表示に切り替わってしまう
  // (`errorUpdatedAt` は過去の失敗タイムスタンプが残るため refetch 中も検出できる。
  // ProcurementPlanDetail/index.tsx の同型コメント参照)。
  const hasNeverSucceeded = data === undefined
  const hasEverFailed = errorUpdatedAt > 0
  const showFetchError = hasNeverSucceeded && hasEverFailed

  const handleReloadClick = (): void => {
    void refetch()
  }

  // BFF response 受信時に draft を初期化
  useEffect(() => {
    if (data?.cells != null) {
      draft.initFromCells(data.cells, data.segmentMetas)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.cells, data?.segmentMetas])

  // ページ遷移時の unsaved guard (設計書 §5)。
  //
  // ブラウザ標準の `window.confirm` は使わない。 8080 の `js/unsaved-guard.js:9`
  // が「旧実装の beforeunload (Chrome 標準ダイアログ) は廃止。 UI 統一感を欠くため」
  // と明記しており、 実際 origin 名 ("localhost:5173 says…") と未翻訳の OK/Cancel が
  // 出て、 他画面の離脱ガード (承認フロー・需要想定詳細) と見た目が揃わない。
  const {
    isBlocked: isLeaveBlocked,
    proceed: proceedLeave,
    cancel: cancelLeave,
  } = useUnsavedGuard({
    beforeUnload: false,
    isDirty: draft.isAnyDirty,
  })

  const tabs = useMemo<Array<{ key: TabKey; label: string }>>(
    () => [
      { key: 'spot_normal', label: t('page:adminBidLimits.tab.spotNormal') },
      { key: 'spot_block', label: t('page:adminBidLimits.tab.spotBlock') },
      { key: 'intraday', label: t('page:adminBidLimits.tab.intraday') },
    ],
    [t],
  )

  // 手数料自動考慮ポリシーのヘルパー (SPOT_NORMAL / INTRADAY のみ populated)
  const getFeeAutoConsiderPolicy = (
    segment: BidSegment,
  ): FeeAutoConsiderPolicy | undefined =>
    data?.feeAutoConsiderPolicies?.find((p) => p.bidSegment === segment)

  // PageScrollArea の中身を showFetchError / tab から 1 つの値に決める
  // (SupplyGap/index.tsx の `chartAreaPlaceholder` と同型)。
  const screenAreaContent: React.ReactElement = showFetchError ? (
    // 全タブが matrix data に依存するため、内容を Alert + 再読み込みに置き換える
    // (ProcurementPlanDetail / SupplyGap と同じ導線)。
    <Flex alignItems="center" justifyContent="center" height="100%">
      <CompactAlert
        actions={
          <TertiaryButton
            loading={isFetching}
            onClick={handleReloadClick}
            size="sm"
          >
            {t('page:adminBidLimits.error.reload')}
          </TertiaryButton>
        }
        description={t('page:adminBidLimits.error.fetchFailedBody')}
        status="error"
        title={t('page:adminBidLimits.error.fetchFailedTitle')}
      />
    </Flex>
  ) : (
    <>
      {/* タブ切替で state 維持: 各タブを hidden 制御するのではなく
            条件レンダリングで mount/unmount するが、
            draft state は page-level (useBidLimitMatrixDraft) で保持するため
            タブ切替でも draft は失われない (設計書 §5 確定) */}
      {tab === 'spot_normal' && (
        <SpotNormalTab
          baseUpdatedAt={draft.baseUpdatedAt[BidSegment.SPOT_NORMAL]}
          cells={data?.cells ?? []}
          draft={draft.drafts[BidSegment.SPOT_NORMAL]}
          feeAutoConsiderPolicy={getFeeAutoConsiderPolicy(
            BidSegment.SPOT_NORMAL,
          )}
          feeUnitPrice={feeUnitPrices.spot}
          isEditing={draft.editing[BidSegment.SPOT_NORMAL]}
          isReadonly={isReadonly}
          marketOrderPolicyEntries={data?.marketOrderPolicyEntries ?? []}
          onCommit={(newDraft) =>
            draft.commitDraft(BidSegment.SPOT_NORMAL, newDraft)
          }
          onReset={() => draft.resetDraft(BidSegment.SPOT_NORMAL)}
          onSetCell={(key, value) =>
            draft.setCell(BidSegment.SPOT_NORMAL, key, value)
          }
          onStartEdit={() => draft.startEditing(BidSegment.SPOT_NORMAL)}
          saved={draft.saved[BidSegment.SPOT_NORMAL]}
        />
      )}

      {tab === 'spot_block' && (
        <SpotBlockTab
          baseUpdatedAt={draft.baseUpdatedAt[BidSegment.SPOT_BLOCK]}
          blockMandatoryPolicy={data?.blockMandatoryPolicy}
          cells={data?.cells ?? []}
          draft={draft.drafts[BidSegment.SPOT_BLOCK]}
          isEditing={draft.editing[BidSegment.SPOT_BLOCK]}
          isReadonly={isReadonly}
          onCommit={(newDraft) =>
            draft.commitDraft(BidSegment.SPOT_BLOCK, newDraft)
          }
          onReset={() => draft.resetDraft(BidSegment.SPOT_BLOCK)}
          onSetCell={(key, value) =>
            draft.setCell(BidSegment.SPOT_BLOCK, key, value)
          }
          onStartEdit={() => draft.startEditing(BidSegment.SPOT_BLOCK)}
          saved={draft.saved[BidSegment.SPOT_BLOCK]}
        />
      )}

      {tab === 'intraday' && (
        <IntradayTab
          baseUpdatedAt={draft.baseUpdatedAt[BidSegment.INTRADAY]}
          cells={data?.cells ?? []}
          defaultWithdrawOffsetSetting={data?.defaultWithdrawOffsetSetting}
          draft={draft.drafts[BidSegment.INTRADAY]}
          feeAutoConsiderPolicy={getFeeAutoConsiderPolicy(BidSegment.INTRADAY)}
          feeUnitPrice={feeUnitPrices.intraday}
          isEditing={draft.editing[BidSegment.INTRADAY]}
          isReadonly={isReadonly}
          marketOrderPolicyEntries={data?.marketOrderPolicyEntries ?? []}
          onCommit={(newDraft) =>
            draft.commitDraft(BidSegment.INTRADAY, newDraft)
          }
          onReset={() => draft.resetDraft(BidSegment.INTRADAY)}
          onSetCell={(key, value) =>
            draft.setCell(BidSegment.INTRADAY, key, value)
          }
          onStartEdit={() => draft.startEditing(BidSegment.INTRADAY)}
          saved={draft.saved[BidSegment.INTRADAY]}
        />
      )}
    </>
  )

  // 上限マトリクスの取得中。
  // 失敗後の再読み込み中はエラー表示を残し、ボタンの loading だけで示す。
  const isMatrixLoading = isLoading && !showFetchError

  const breadcrumbItems = [
    { label: t('common:nav.bidding') },
    { label: t('common:route.adminBidLimits'), bold: true },
  ]

  return (
    <WorkflowPageLayout breadcrumbItems={breadcrumbItems}>
      <PageBody>
        {/* ページ説明 (design admin/bid-limits.html `.page-description`)。
            「需要グループ」は design だと内部リンクだが、対応画面が未実装で遷移先が
            無いため、文言だけ残しリンクは張らない (画面実装時にリンク化する)。 */}
        <PageIntro
          description={
            <>
              {t('page:adminBidLimits.description.prefix')}
              <Link asChild>
                <RouterLink to={routePaths.biddingSpot.path}>
                  {t('page:adminBidLimits.description.spotLink')}
                </RouterLink>
              </Link>
              {t('page:adminBidLimits.description.between')}
              <Link asChild>
                <RouterLink to={routePaths.biddingIntraday.path}>
                  {t('page:adminBidLimits.description.intradayLink')}
                </RouterLink>
              </Link>
              {t('page:adminBidLimits.description.suffix')}
            </>
          }
        />
        <Tabs
          items={tabs}
          onSelect={setTab}
          selected={tab}
          tabSx={{ fontSize: tabTriggerFontSize.raw }}
        />
        <RegionLoading
          flexDirection="column"
          flex="1"
          display="flex"
          minHeight={isMatrixLoading ? '200px' : 0}
          isLoading={isMatrixLoading}
        >
          {isMatrixLoading ? null : (
            <PageScrollArea>{screenAreaContent}</PageScrollArea>
          )}
        </RegionLoading>
      </PageBody>
      {/* 未保存離脱ガード。 DS Modal ベースの共通ダイアログで出す。 */}
      <ConfirmDialog
        body={t('page:adminBidLimits.unsavedGuard.message')}
        confirmLabel={t('page:adminBidLimits.unsavedGuard.confirm')}
        isOpen={isLeaveBlocked}
        onCancel={cancelLeave}
        onConfirm={proceedLeave}
        title={t('page:adminBidLimits.unsavedGuard.title')}
      />
    </WorkflowPageLayout>
  )
}
